import { Dispatch, SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISelectedData, ITreeItemES } from "../../interfaces/tree";

import useToast from "../useToast";

import { deleteProgram } from "../../services/programs";

export interface IPayload {
    id: number;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    setSelected: Dispatch<SetStateAction<ISelectedData | null>>;
    setExpanded: Dispatch<SetStateAction<ITreeItemES[]>>;
};

export interface IResponse {
    error?: string;
    message: string;
    statusCode: number;
};

const useDeleteProgram = () => {
    const queryClient = useQueryClient();

    const { addToast } = useToast();

    const mutationFn = async (data: IPayload): Promise<IResponse> => {
        return await deleteProgram(data.id);
    };

    return useMutation({
        mutationFn,
        onSuccess: (response: IResponse, variables: IPayload) => {
            if (response?.statusCode === 200) {
                addToast({
                    type: 'success',
                    title: 'Program deleted successfully.',
                });

                variables.setSelected(null);
                variables.setExpanded(prevData => {
                    const newData = [...prevData];

                    const index = newData.findIndex(item => item.id === variables.id && item.level === 0);
                    if (index > -1) {
                        newData.splice(index, 1);
                    };

                    return newData;
                });

                queryClient.invalidateQueries({ queryKey: ['programs_tree'] });
            };
        },
        onError: (error: IResponse, variables: IPayload) => {
            addToast({
                type: 'error',
                title: 'Error occured',
                message: error.message
            });
        },
        onSettled: (data: IResponse | undefined, error: IResponse | null, variables: IPayload) => {
            variables.setIsProcessing(false);
        },
    });
};

export default useDeleteProgram;